<template>
    <div class="sms-ai-draft-controls">
        <sms-ai-draft-alert
            :is-failed="state.isFailed.value"
            :is-rate-limited="state.isRateLimited.value"
            :rate-limit-seconds="rateLimitSeconds"
            v-if="state.showAlert.value"
        />

        <ai-draft-unsupported-alert
            class="mb-[6px] sm:mb-2"
            data-testid="sms-ai-unsupported-alert"
            :dismiss-label="$t('smsCommunication.aiDraft.unsupportedDismiss')"
            :message="$t('smsCommunication.aiDraft.unsupportedUpdate')"
            @dismiss="dismissUnsupportedAlert"
            v-if="showUnsupportedAlert"
        />

        <sms-ai-draft-update-panel
            :can-edit-instruction="state.canEditInstruction.value"
            :can-submit-update="canSubmitUpdate"
            :can-use-undo="state.canUseUndo.value"
            :show-service-lead-helper="state.showUpdateServiceLeadHelper.value"
            :model-value="instruction"
            @submit="updateDraft"
            @undo="emit('undo')"
            @update:model-value="instruction = $event"
            v-if="state.showUpdatePanel.value"
        />

        <sms-ai-draft-action-row
            :generate-disabled="state.generateDisabled.value"
            :show-generate-button="state.showGenerateButton.value"
            :show-generate-service-lead-helper="state.showGenerateServiceLeadHelper.value"
            @generate="emit('generate')"
            v-else-if="state.showActionRow.value"
        />
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import AiDraftUnsupportedAlert from '@/components/aiDraft/AiDraftUnsupportedAlert.vue';
    import SmsAiDraftActionRow from '@/components/sms/aiDraft/SmsAiDraftActionRow.vue';
    import SmsAiDraftAlert from '@/components/sms/aiDraft/SmsAiDraftAlert.vue';
    import SmsAiDraftUpdatePanel from '@/components/sms/aiDraft/SmsAiDraftUpdatePanel.vue';
    import { useSmsAiDraftControlsState } from '@/components/sms/aiDraft/useSmsAiDraftControlsState.ts';
    import { SmsAiDraftState } from '@/components/sms/aiDraft/types.ts';

    const props = withDefaults(
        defineProps<{
            canGenerate: boolean;
            canUndo?: boolean;
            canUpdate?: boolean;
            disabled?: boolean;
            hasGeneratedDraft: boolean;
            hasText?: boolean;
            instructionResetKey?: number;
            rateLimitSeconds: number;
            serviceLeadDisabled: boolean;
            state: SmsAiDraftState;
        }>(),
        {
            canUndo: false,
            canUpdate: false,
            disabled: false,
            hasText: false,
            instructionResetKey: 0,
        }
    );

    const emit = defineEmits<{
        generate: [];
        panelResize: [];
        undo: [];
        update: [payload: { instruction: string }];
    }>();

    const state = useSmsAiDraftControlsState(props);
    const instruction = ref('');
    const unsupportedDismissed = ref(false);
    const isUnsupported = computed<boolean>(
        () => props.state === SmsAiDraftState.UpdateUnsupported
    );
    const showUnsupportedAlert = computed<boolean>(
        () => isUnsupported.value && !unsupportedDismissed.value
    );
    // Pre-flush so the host reads geometry before the update panel mounts or unmounts.
    watch(state.showUpdatePanel, () => {
        emit('panelResize');
    });

    watch(showUnsupportedAlert, () => {
        emit('panelResize');
    });

    const trimmedInstruction = computed<string>(() => instruction.value.trim());
    const hasInstruction = computed<boolean>(() => !!trimmedInstruction.value);
    const canSubmitUpdate = computed<boolean>(
        () =>
            state.canEditInstruction.value &&
            hasInstruction.value &&
            instruction.value.length <= 255
    );

    watch(
        () => props.instructionResetKey,
        () => {
            instruction.value = '';
        }
    );

    watch(
        () => props.state,
        (nextState) => {
            if (nextState === SmsAiDraftState.Loading) {
                unsupportedDismissed.value = false;
            }
        }
    );

    const updateDraft = () => {
        if (!canSubmitUpdate.value) {
            return;
        }

        emit('update', { instruction: trimmedInstruction.value });
    };

    const dismissUnsupportedAlert = () => {
        unsupportedDismissed.value = true;
    };
</script>
